import { Link, useLocation } from "react-router";
import { X, MoveLeft } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { agentSidebarLinks } from "./sidebarLinks";
import VersionTypewriter from "../../common/versionTypewriter";
import { getSalesPortalVersionString } from "../../../lib/version";

interface AgentSidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

const AgentSidebar = ({
  isOpen,
  toggleSidebar,
  onCollapseChange,
}: AgentSidebarProps) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    onCollapseChange?.(next);
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 ${isCollapsed ? "w-16" : "w-64"}
        bg-[#090807] text-[#F1EDE4] border-r border-[#2A2622]
        flex flex-col h-screen transform transition-all duration-300 ease-in-out z-30
        ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
    >
      {/* Close btn - mobile only */}
      <button
        className="absolute top-2.5 right-2.5 lg:hidden z-50 p-1.5 rounded-lg bg-[#B87F4B]/10 hover:bg-[#B87F4B]/20"
        onClick={toggleSidebar}
        aria-label="Close menu"
      >
        <X size={16} className="text-[#D19A66] transition-colors" />
      </button>

      {/* Logo — matches AgentLoginPage branding */}
      <div
        className={`${isCollapsed ? "px-2 py-4" : "px-4 py-4"} border-b border-[#2A2622] flex-shrink-0`}
      >
        <Link to="/dashboard" className="block">
          <div
            className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2.5"}`}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#B87F4B]/35 bg-[#B87F4B]/08 text-sm font-display text-[#D19A66] shadow-[0_0_16px_rgba(184,127,75,0.15)] flex-shrink-0">
              ◆
            </span>
            {!isCollapsed && (
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-semibold text-[#F1EDE4] tracking-tight leading-tight">
                  Save In Gold
                </span>
                <span className="text-[9px] text-[#D19A66]/85 uppercase tracking-[0.18em]">
                  Sales Portal
                </span>
              </div>
            )}
          </div>
        </Link>
      </div>

      {/* Nav — flat, no submenu, no badges */}
      <motion.nav layoutScroll className="flex-1 overflow-y-auto py-3 px-2 min-h-0">
        <ul className="space-y-1">
          {agentSidebarLinks.map((item) => (
            <li key={item.id}>
              <Link
                to={item.path}
                onClick={toggleSidebar}
                title={isCollapsed ? item.label : undefined}
                className={`relative isolate flex items-center rounded-lg border border-transparent transition-all duration-150 group
                  ${isCollapsed ? "justify-center w-10 h-10 mx-auto p-0" : "py-2.5 px-3 gap-2.5"}
                  ${
                    isActive(item.path)
                      ? "text-[#F1EDE4] font-medium"
                      : "hover:bg-[#B87F4B]/08 text-[#ADA79A] hover:text-[#F1EDE4]"
                  }`}
              >
                {isActive(item.path) && (
                  <motion.span
                    layoutId="agent-sidebar-active"
                    className="absolute inset-0 -z-10 rounded-lg bg-gradient-to-r from-[#DDB083]/15 to-[#B87F4B]/10 border border-[#B87F4B]/30 shadow-[0_0_12px_rgba(184,127,75,0.08)]"
                  />
                )}
                <span
                  className={`flex-shrink-0 ${isActive(item.path) ? "text-[#D19A66]" : "text-[#6B6559] group-hover:text-[#D19A66]"} transition-colors`}
                >
                  {item.icon}
                </span>
                {!isCollapsed && (
                  <span className="truncate text-sm">{item.label}</span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      </motion.nav>

      {/* Collapse button and the sales portal version */}
      <div
        className={`border-t border-[#2A2622] flex-shrink-0 ${isCollapsed ? "p-1.5" : "p-2"}`}
      >
        <button
          onClick={toggleCollapse}
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          className={`w-full flex items-center justify-center py-2.5 px-3 bg-gradient-to-b from-[#DDB083] to-[#B87F4B] hover:brightness-[1.05] text-[#1C1006] rounded-lg transition-all duration-200 group ${isCollapsed ? "p-2" : ""}`}
        >
          <div
            className={`flex items-center ${isCollapsed ? "justify-center" : "gap-2"}`}
          >
            <MoveLeft
              size={16}
              strokeWidth={2.5}
              className={`transform transition-transform duration-300 group-hover:scale-110 ${isCollapsed ? "rotate-180" : "rotate-0"}`}
            />
            {!isCollapsed && (
              <span className="text-xs font-semibold">Collapse</span>
            )}
          </div>
        </button>
        {!isCollapsed && (
          <p className="mt-1.5 text-center">
            <VersionTypewriter text={getSalesPortalVersionString()} colorClass="text-[#D19A66]" />
          </p>
        )}
      </div>
    </aside>
  );
};

export default AgentSidebar;
